import React, { useState, useMemo, useEffect } from 'react';
import { List, Tabs, Badge } from 'antd';
import { useDispatch } from 'react-redux';
import ConversationListItem from './ConversationListItem';
import { isEmpty } from '../helper';
import { addUnreadBooleanAction } from '../actions/unreadMessageAction';

const { TabPane } = Tabs;

const sortConversationsByFriendlyName = (a, b) => {
  return a.baseConversation.friendlyName
    .toUpperCase()
    .localeCompare(b.baseConversation.friendlyName.toUpperCase());
};

const sortConversationsByRecent = (a, b) => {
  if (isEmpty(a.lastMessage)) {
    return -1;
  }
  if (isEmpty(b.lastMessage)) {
    return 1;
  }
  return (
    new Date(b.lastMessage.state.timestamp) -
    new Date(a.lastMessage.state.timestamp)
  );
};

const sortConversations = (convoComponentsList) => {
  const emptyConversations = [];
  const nonEmptyConversations = [];
  convoComponentsList.forEach((convo) => {
    if (isEmpty(convo.lastMessage)) {
      emptyConversations.push(convo);
    } else {
      nonEmptyConversations.push(convo);
    }
  });
  return emptyConversations
    .sort(sortConversationsByFriendlyName)
    .concat(nonEmptyConversations.sort(sortConversationsByRecent));
};

const filterByStatus = (convoComponentsList, status) => {
  return convoComponentsList.filter((item) => {
    const ticketHistory =
      item.baseConversation.channelState.attributes?.ticket_history;
    if (ticketHistory) {
      return ticketHistory[ticketHistory.length - 1].status === status;
    }
    return null;
  });
};

// TODO Use Twilio's read horizon instead of storing read receipts in attributes
// Conversation is "read" if:
// - It has no messages
// - Sender of last message is user or System Message
// - Read receipt exists for user and read_receipt timestamp is later than last message timestamp
const checkIfRead = (convoComponentsObj, email) => {
  const {
    lastMessage,
    baseConversation: {
      channelState: { attributes },
    },
  } = convoComponentsObj;

  const lastSeenAt =
    attributes?.read_receipts?.[email] &&
    Date.parse(attributes.read_receipts[email]);

  if (!isEmpty(lastMessage)) {
    if (lastMessage.author === email || lastMessage.author === 'System Message')
      return true;
    const lastMessageUnixTimestamp =
      Date.parse(lastMessage.state.timestamp) || null;
    if (lastSeenAt) {
      return lastSeenAt > lastMessageUnixTimestamp;
    }
    return false;
  }
  return true;
};

const getUnreadCount = (convoComponentsList, email) => {
  const filteredByUnread = convoComponentsList.filter((c) => {
    return !checkIfRead(c, email);
  });
  return filteredByUnread.length;
};

const tabToStatusMap = {
  '0': 'New',
  '1': 'In Progress',
  '2': 'Resolved',
};

const ConversationList = ({
  conversationComponents,
  selectedConversationSid,
  onChannelClick,
  email,
  isListLoading,
  handleTicketUpdate,
}) => {
  const dispatch = useDispatch();
  const ticketHistoryString = JSON.stringify(
    conversationComponents.map(
      (item) => item.baseConversation.channelState.attributes.ticket_history,
    ),
  );

  const readReceiptsString = JSON.stringify(
    conversationComponents.map(
      (item) => item.baseConversation.channelState.attributes.read_receipts,
    ),
  );

  const sortedByRecent = useMemo(
    () => sortConversations(conversationComponents),
    [conversationComponents],
  );
  const [activeTab, setActiveTab] = useState('0');

  const tabConversationsList = useMemo(() => {
    return filterByStatus(sortedByRecent, tabToStatusMap[activeTab]);
  }, [activeTab, sortedByRecent, ticketHistoryString, readReceiptsString]);

  const [
    newUnreadCount,
    inProgressUnreadCount,
    resolvedUnreadCount,
  ] = useMemo(
    () => [
      getUnreadCount(
        filterByStatus(sortedByRecent, tabToStatusMap['0']),
        email,
      ),
      getUnreadCount(
        filterByStatus(sortedByRecent, tabToStatusMap['1']),
        email,
      ),
      getUnreadCount(
        filterByStatus(sortedByRecent, tabToStatusMap['2']),
        email,
      ),
    ],
    [email, sortedByRecent, ticketHistoryString, readReceiptsString],
  );

  useEffect(() => {
    if (!isListLoading && conversationComponents.length > 0) {
      dispatch(addUnreadBooleanAction(newUnreadCount + inProgressUnreadCount));
    }
  }, [newUnreadCount, inProgressUnreadCount]);

  const conversationList = useMemo(
    () => (
      <List
        bordered
        locale={{ emptyText: ' ' }}
        loading={isListLoading}
        dataSource={tabConversationsList}
        renderItem={(item) => {
          return (
            <ConversationListItem
              item={item}
              selectedConversationSid={selectedConversationSid}
              onChannelClick={onChannelClick}
              email={email}
              handleTicketUpdate={handleTicketUpdate}
              checkIfRead={checkIfRead}
            />
          );
        }}
      />
    ),
    [
      tabConversationsList,
      handleTicketUpdate,
      isListLoading,
      onChannelClick,
      email,
      selectedConversationSid,
    ],
  );

  const tabLabel = (label, count) => {
    return (
      <Badge
        style={{ backgroundColor: '#1890ff' }}
        count={count}
        offset={[10, -1]}
      >
        {label}
      </Badge>
    );
  };

  return (
    <Tabs
      defaultActiveKey={activeTab}
      onChange={(tab) => setActiveTab(tab)}
      size="small"
      tabBarGutter={30}
    >
      <TabPane tab={tabLabel('New', newUnreadCount)} key="0">
        {conversationList}
      </TabPane>
      <TabPane tab={tabLabel('In Progress', inProgressUnreadCount)} key="1">
        {conversationList}
      </TabPane>
      <TabPane tab={tabLabel('Resolved', resolvedUnreadCount)} key="2">
        {conversationList}
      </TabPane>
    </Tabs>
  );
};

export default ConversationList;
